import { useState, useEffect } from "react";
import "../styles/header.css";
import { MODULES } from "../data/moduleTransitions.js";
import useDealStore from "../store/dealStore";
import useAuthStore from "../store/authStore";
import useOrganizationStore from "../store/organizationStore";

function Header() {
  const { getDealSummary, dealState, navigateToModule, getUnreadCount, resetDeal, toggleMarketIntelligence, marketIntelligenceOpen, checkDealValidation } = useDealStore();
  const { user, logout } = useAuthStore();
  const { profile, isProfileComplete } = useOrganizationStore();
  const dealSummary = getDealSummary();
  const unreadCount = getUnreadCount();
  const dealName = dealSummary.dealName || "No Deal Selected";
  const dealLocation = dealSummary.location || "";

  useEffect(() => {
    // Initial check on mount/deal change
    checkDealValidation();

    // Poll every 10s for validation updates in the background
    const interval = setInterval(() => {
      checkDealValidation();
    }, 10000);

    return () => clearInterval(interval);
  }, [dealState?.dealId]);

  // ── New Deal confirmation state ──────────────────────────────────────────
  const [showNewDealConfirm, setShowNewDealConfirm] = useState(false);

  const normalizeHeaderText = (value = "") =>
    value.toString().replace(/[^a-z0-9]+/gi, "").toLowerCase();
  const shouldShowDealTitle =
    !dealSummary.dealId ||
    !dealLocation ||
    normalizeHeaderText(dealName) !== normalizeHeaderText(dealLocation);

  const handleLogout = () => {
    resetDeal();
    logout();
  };

  const handleNewDeal = () => {
    // If there's an active deal in progress, ask for confirmation first
    if (dealState.dealId) {
      setShowNewDealConfirm(true);
    } else {
      resetDeal();
      navigateToModule('entry');
    }
  };

  const confirmNewDeal = () => {
    resetDeal();
    setShowNewDealConfirm(false);
    navigateToModule('entry');
  };

  return (
    <>
      {/* ── New Deal confirmation modal ─────────────────────────────────────── */}
      {showNewDealConfirm && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          background: 'rgba(0,0,0,0.45)', display: 'flex',
          alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{
            background: '#fff', borderRadius: 12, padding: '32px 36px',
            maxWidth: 420, width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
            textAlign: 'center',
          }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>🔄</div>
            <h3 style={{ margin: '0 0 10px', fontSize: 18, color: '#1B3A6B', fontWeight: 700 }}>
              Switch to a New Deal?
            </h3>
            <p style={{ margin: '0 0 24px', color: '#64748b', fontSize: 14, lineHeight: 1.6 }}>
              Your current deal (<strong>{dealName}</strong>) and all its data will be cleared.
              You will stay logged in and can start a fresh deal immediately.
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
              <button
                onClick={() => setShowNewDealConfirm(false)}
                style={{
                  padding: '10px 24px', borderRadius: 8, border: '1.5px solid #cbd5e1',
                  background: '#fff', color: '#374151', fontWeight: 600,
                  cursor: 'pointer', fontSize: 14,
                }}
              >
                Cancel — Keep Working
              </button>
              <button
                onClick={confirmNewDeal}
                style={{
                  padding: '10px 24px', borderRadius: 8, border: 'none',
                  background: '#1B3A6B', color: '#fff', fontWeight: 600,
                  cursor: 'pointer', fontSize: 14,
                }}
              >
                Yes, Start New Deal
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="header">
        <div className="header-left">
          {shouldShowDealTitle && (
            <h2 className="deal-title">
              {dealName}
            </h2>
          )}

        </div>

        <div className="header-center">
        </div>

        <div className="header-right">
          {/* ── New Deal button — always visible, lets user switch without logout ── */}
          <button
            className="dashboard-button"
            onClick={handleNewDeal}
            title="Clear current deal and start a new one — no logout needed"
            style={{
              background: '#f0f9ff', color: '#0369a1',
              border: '1.5px solid #bae6fd', fontWeight: 700,
              display: 'flex', alignItems: 'center', gap: 6,
            }}
          >
            ＋ New Deal
          </button>

          <button
            className={`market-intel-button ${marketIntelligenceOpen ? 'active' : ''}`}
            onClick={toggleMarketIntelligence}
            title="Market Intelligence — always accessible"
          >
            📊 Market Intelligence
          </button>

          <button className="dashboard-button" onClick={() => navigateToModule('dashboard')}>
            Deal Dashboard
            {unreadCount > 0 && (
              <span className="header-notif-badge">{unreadCount}</span>
            )}
          </button>

          {/* Company name button — shows org name when set, warning when not */}
          {isProfileComplete && profile.companyName ? (
            <button
              className="dashboard-button"
              onClick={() => navigateToModule('org-profile')}
              title="Organization Profile Settings"
              style={{
                background: 'transparent',
                border: `1.5px solid ${profile.brandColor || '#1B3A6B'}`,
                color: profile.brandColor || '#1B3A6B',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontWeight: 700,
              }}
            >
              <span style={{
                width: 8, height: 8, borderRadius: '50%',
                background: profile.brandColor || '#1B3A6B',
                display: 'inline-block', flexShrink: 0,
              }} />
              {profile.companyName}
            </button>
          ) : (
            <button
              className="dashboard-button"
              onClick={() => navigateToModule('org-profile')}
              title="Complete your Organization Profile"
              style={{ background: '#f59e0b', color: '#fff', border: 'none' }}
            >
              ⚠️ Setup Org Profile
            </button>
          )}

          <div className="user-info">
            <div className="user-avatar">
              {user?.name?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            <div className="user-details">
              <span className="user-name">{user?.name || 'User'}</span>
              <span className="user-role">{user?.role || 'Investor'}</span>
            </div>
          </div>

          <button className="logout-button" onClick={handleLogout}>
            Logout
          </button>
        </div>
      </div>
    </>
  );
}

export default Header;
